import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import bs58 from 'bs58';
import { Connection, PublicKey } from '@solana/web3.js';
import {
  type Network,
  type TokenHolding,
  getSolBalance,
  getTokenHoldings,
  rpcUrl,
} from '../solana';
import { type DecodedExport, bytesToHex } from '../localSign';

/**
 * The ONLY place the decoded private key is stored at runtime.
 *
 * `scalar` and `pubkey` live in `useState` here and nowhere else. There is no:
 *   - localStorage / sessionStorage / IndexedDB / cookie write
 *   - analytics or error-reporting SDK that could capture them
 *   - global window.* assignment of the bytes
 *
 * A page refresh discards the key — the user must re-paste. Intentional.
 */

export const MAX_SIGNATURES = 50;

export type Account = {
  scalar: Uint8Array;
  pubkey: Uint8Array;
  format: DecodedExport['format'];
};

export interface TxResult {
  kind: 'sol' | 'spl';
  signature: string;
  network: Network;
}

export interface SigResult {
  message: string;
  signatureHex: string;
  verified: boolean;
}

interface AccountContextValue {
  account: Account | null;
  setAccount: (a: Account | null) => void;
  address: string;
  scalarHex: string;
  scalarBase58: string;

  network: Network;
  setNetwork: (n: Network) => void;
  rpcOverride: string;
  setRpcOverride: (s: string) => void;
  connection: Connection;

  solBalance: number | null;
  solBalanceError: string | null;
  holdings: TokenHolding[];
  holdingsError: string | null;
  refreshing: boolean;
  refreshBalances: () => Promise<void>;

  txResults: TxResult[];
  pushTx: (r: TxResult) => void;

  sigResults: SigResult[];
  pushSig: (r: SigResult) => void;

  resetSession: () => void;
}

const AccountContext = createContext<AccountContextValue | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
  const [account, setAccountState] = useState<Account | null>(null);
  const [network, setNetwork] = useState<Network>('mainnet');
  const [rpcOverride, setRpcOverride] = useState('');

  const [solBalance, setSolBalance] = useState<number | null>(null);
  const [solBalanceError, setSolBalanceError] = useState<string | null>(null);
  const [holdings, setHoldings] = useState<TokenHolding[]>([]);
  const [holdingsError, setHoldingsError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [txResults, setTxResults] = useState<TxResult[]>([]);
  const [sigResults, setSigResults] = useState<SigResult[]>([]);

  const connection = useMemo(
    () => new Connection(rpcUrl(network, rpcOverride), 'confirmed'),
    [network, rpcOverride],
  );

  const address = useMemo(() => (account ? bs58.encode(account.pubkey) : ''), [account]);
  const scalarHex = useMemo(() => (account ? bytesToHex(account.scalar) : ''), [account]);
  const scalarBase58 = useMemo(() => (account ? bs58.encode(account.scalar) : ''), [account]);

  const refreshBalances = useCallback(async () => {
    if (!account) return;
    setRefreshing(true);
    setSolBalanceError(null);
    setHoldingsError(null);
    const owner = new PublicKey(account.pubkey);
    const [balRes, holdRes] = await Promise.allSettled([
      getSolBalance(connection, owner),
      getTokenHoldings(connection, owner),
    ]);
    if (balRes.status === 'fulfilled') {
      setSolBalance(balRes.value);
    } else {
      setSolBalance(null);
      setSolBalanceError(balRes.reason instanceof Error ? balRes.reason.message : String(balRes.reason));
    }
    if (holdRes.status === 'fulfilled') {
      setHoldings(holdRes.value);
    } else {
      setHoldings([]);
      setHoldingsError(holdRes.reason instanceof Error ? holdRes.reason.message : String(holdRes.reason));
    }
    setRefreshing(false);
  }, [account, connection]);

  const pushTx = useCallback((r: TxResult) => setTxResults(prev => [r, ...prev]), []);
  const pushSig = useCallback(
    (r: SigResult) => setSigResults(prev => [r, ...prev].slice(0, MAX_SIGNATURES)),
    [],
  );

  const resetSession = useCallback(() => {
    setAccountState(null);
    setSolBalance(null);
    setSolBalanceError(null);
    setHoldings([]);
    setHoldingsError(null);
    setRefreshing(false);
    setTxResults([]);
    setSigResults([]);
    setRpcOverride('');
  }, []);

  const setAccount = useCallback((a: Account | null) => {
    if (a === null) {
      resetSession();
      return;
    }
    setAccountState(a);
    setSolBalance(null);
    setSolBalanceError(null);
    setHoldings([]);
    setHoldingsError(null);
    setTxResults([]);
    setSigResults([]);
  }, [resetSession]);

  const value: AccountContextValue = {
    account,
    setAccount,
    address,
    scalarHex,
    scalarBase58,
    network,
    setNetwork,
    rpcOverride,
    setRpcOverride,
    connection,
    solBalance,
    solBalanceError,
    holdings,
    holdingsError,
    refreshing,
    refreshBalances,
    txResults,
    pushTx,
    sigResults,
    pushSig,
    resetSession,
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error('useAccount must be used inside AccountProvider');
  return ctx;
}
